import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import SidebarItem from "./SidebarItem";
import "./Sidebar.scss";
import { useAuth } from "../../contexts/AuthContext"; // Adjust the path if needed

const Sidebar = ({ notificationCounts = {}, onNavigate, isOpen = false }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const location = useLocation();

  // Get authorities and user info from auth context
  const { getUserAuthorities, isCreator } = useAuth();

  const userRoles = getUserAuthorities();

  const menuItems = [
    {
      name: "Dashboard",
      icon: "material-symbols-light:dashboard-outline",
      to: "/dashboard",
      notifications: notificationCounts.dashboard || 0,
      roles: ["ROLE_USER"], // Everyone
    },
    {
      name: "Discover",
      icon: "material-symbols:psychology-outline",
      to: "/discovermoretests",
      notifications: notificationCounts.discover || 0,
      roles: ["ROLE_USER"], // Users only
    },
    {
      name: "Analytics",
      icon: "material-symbols:trending-up",
      to: "/analytics",
      notifications: notificationCounts.growth || 0,
      roles: ["ROLE_USER"], // Admin only
    },

    ...(isCreator
      ? [
          {
            name: "Test Management",
            icon: "material-symbols:admin-panel-settings-outline",
            to: "/testmanagement",
            notifications: notificationCounts.admin || 0,
            roles: ["ROLE_USER"],
          },
        ]
      : []),
  ];

  // Filter menu items based on user roles
  const filteredMenuItems = menuItems.filter((item) =>
    item.roles.some((role) => userRoles.includes(role)),
  );

  const isActive = (item) => {
    if (location.pathname === item.to) {
      return true;
    }

    // Check if current path is under a main menu item's path
    if (
      (location.pathname.includes("/dashboard") && item.name === "Dashboard") ||
      (location.pathname.includes("/discover") && item.name === "Discover") ||
      (location.pathname.includes("/journey") && item.name === "My Journey") ||
      (location.pathname.includes("/insights") && item.name === "Insights") ||
      (location.pathname.includes("/growth") && item.name === "Growth Areas") ||
      (location.pathname.includes("/achievements") &&
        item.name === "Achievements") ||
      (location.pathname.includes("/profile") && item.name === "Profile") ||
      (location.pathname.includes("/admin") && item.name === "Admin")
    ) {
      return true;
    }

    // Check submenu paths
    if (item.submenu) {
      return item.submenu.some((subItem) => {
        if (location.pathname === subItem.to) {
          return true;
        }

        // Check nested submenu
        if (subItem.submenu) {
          return subItem.submenu.some(
            (nestedItem) => location.pathname === nestedItem.to,
          );
        }

        return false;
      });
    }

    return false;
  };

  // Handle navigation and notification clearing
  const handleItemClick = (item) => {
    setHoveredItem(item.name);
    onNavigate?.(item);
  };

  return (
    <div className={`sidebar ${isOpen ? "sidebar--open" : ""}`}>
      <ul className="sidebar-menu">
        {filteredMenuItems.map((item) => {
          // Map the item name to the notification count key
          const sectionKey = {
            Dashboard: "dashboard",
            Discover: "discover",
            "My Journey": "journey",
            Insights: "insights",
            "Growth Areas": "growth",
            Achievements: "achievements",
            Profile: "profile",
            Admin: "admin",
          }[item.name];

          // Get the notification count for this item
          const notificationCount = notificationCounts[sectionKey] || 0;

          return (
            <SidebarItem
              key={item.name}
              item={item}
              isHovered={hoveredItem === item.name}
              isActive={isActive(item)}
              onHover={setHoveredItem}
              onClick={() => handleItemClick(item)}
              userRoles={userRoles}
              notificationCount={notificationCount}
            />
          );
        })}
      </ul>
    </div>
  );
};

export default Sidebar;
